import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { renderInstanceLabel } from '@bmo/form-core';
import { Button, InputNumber, Select, Tooltip, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type FileEntryCondition,
  type FormSection,
  type SelectOption,
  type WorkflowField,
  type WorkflowStep,
} from '@/api/workflows';
import { BmoModal } from '@/components/BmoModal';
import { ConfirmPopover } from '@/components/ConfirmPopover';
import { ICON_BTN_PROPS } from '@/components/iconButton';

import { type ConditionScope, type EligibleSection, eligibleSourceFields } from './sourceFields';

/** `Select` value prefix marking a picked source as a repeatable SECTION
 *  (its instance count) rather than a field. Field ids and section ids are
 *  both GUIDs from independent namespaces, so a flat value has no other way
 *  to disambiguate what the originator picked. */
const SECTION_SOURCE_PREFIX = 'section:';
const sectionSourceValue = (sectionId: string): string => `${SECTION_SOURCE_PREFIX}${sectionId}`;
const sectionIdFromSourceValue = (value: string): string | null =>
  value.startsWith(SECTION_SOURCE_PREFIX) ? value.slice(SECTION_SOURCE_PREFIX.length) : null;

const { Text } = Typography;

// Slightly smaller icon button — matches the in-row gear/✕ on the PDF rows so
// the visual rhythm is the same everywhere this modal renders.
const iconBtn = {
  ...ICON_BTN_PROPS,
  style: { ...ICON_BTN_PROPS.style, width: 26, height: 26, fontSize: 12, borderRadius: 6 },
} as const;

export type ConditionOperator =
  | 'equals'
  | 'not_equals'
  | 'is_true'
  | 'is_false'
  | 'contains'
  | 'not_contains'
  | 'age_years_lt'
  | 'age_years_ge'
  | 'age_years_between'
  | 'count_eq'
  | 'count_ge';

/** The one RANGE operator: `value` is the inclusive lower bound and `valueMax` the
 *  exclusive upper one, so "aged 6 up to the day before their 9th birthday" is 6 / 9.
 *  Both bounds ride one row because they must bind to the same repeatable instance —
 *  see `evaluateConditions.ts`'s case for the family-of-3-and-12 defect that splitting
 *  them across two `any`-scoped rows produces. */
const AGE_RANGE_OPERATOR = 'age_years_between' satisfies ConditionOperator;

/** Operators whose value is a whole-year threshold rather than a picked
 *  option — driven by a number input instead of the options Select. The range
 *  operator is one of these and additionally drives the second bound input. */
const AGE_OPERATORS = new Set<ConditionOperator>([
  'age_years_lt',
  'age_years_ge',
  AGE_RANGE_OPERATOR,
]);

/** Operators whose SOURCE is a repeatable section's instance count rather
 *  than a field value — see `count_eq`/`count_ge` in `evaluateConditions.ts`.
 *  Value is a non-negative integer threshold, driven by a number input like
 *  the age operators. The only operators ever offered once a SECTION (rather
 *  than a field) is picked as the source — see `pickOperators` below. */
const COUNT_OPERATORS: ConditionOperator[] = ['count_eq', 'count_ge'];

/**
 * The `instanceScope` choices for a source inside `section`: the two quantifiers, then one
 * entry per instance the section can hold, labelled exactly as the advisor sees it
 * ("Accountholder 1", "Accountholder 2") via the shared `renderInstanceLabel`.
 *
 * Indexes are only enumerable when the section carries a `maxInstances` cap — an unbounded
 * section has no last instance to stop at, and inventing a ceiling would offer an index the
 * advisor may never create. Those sections get the quantifiers alone.
 *
 * `current` is always included even when it falls outside that range, so lowering a
 * section's cap leaves an existing rule visible and editable rather than blanking the Select
 * and writing the blank back on the next commit.
 */
export function instanceScopeOptions(
  section: FormSection,
  current: string | null,
  language: 'en' | 'fr',
  t: (key: string) => string,
  ns: string,
): { value: string; label: string }[] {
  const max = typeof section.maxInstances === 'number' ? section.maxInstances : 0;
  const indexes = new Set<number>();
  for (let i = 0; i < max; i++) indexes.add(i);
  if (current !== null && /^\d+$/.test(current)) indexes.add(Number.parseInt(current, 10));
  return [
    { value: 'any', label: t(`${ns}.instanceScopeAny`) },
    { value: 'all', label: t(`${ns}.instanceScopeAll`) },
    ...[...indexes]
      .sort((a, b) => a - b)
      .map((i) => ({ value: String(i), label: renderInstanceLabel(section, i, language) })),
  ];
}

/** Which operators are valid for a given source-field type. */
export function operatorsFor(field: WorkflowField | null | undefined): ConditionOperator[] {
  if (!field) return [];
  if (field.type === 'Toggle' || field.type === 'Checkbox') return ['is_true', 'is_false'];
  // CheckboxGroup holds an array of option values — the only sensible
  // operators are "the array contains X" / "the array doesn't contain X".
  // Equals/not_equals would stringify the array and compare against a
  // single value, which never matches.
  if (field.type === 'CheckboxGroup') return ['contains', 'not_contains'];
  // Date sources compare on computed age, not the raw ISO string — equality
  // on a birthdate is never what an originator wants. See
  // `evaluateConditions.ts`'s `age_years_lt`/`age_years_ge` handling.
  if (field.type === 'Date') return ['age_years_ge', 'age_years_lt', AGE_RANGE_OPERATOR];
  return ['equals', 'not_equals'];
}

/**
 * Modal for editing a list of gating conditions + a shared all/any combinator.
 * Pure-controlled: parent owns persistence and passes `onSave`. Used by
 * the PDF entries drawer (gate which PDFs get populated), the field
 * properties panel (gate when a field is visible), and the canvas section
 * header (gate when a section is visible).
 *
 * The modal is open when `open=true` AND `allSteps` has at least one
 * DynamicForm step with an eligible field; otherwise the picker is disabled.
 *
 * `scope` defaults to PDF (every step, every eligible field). Field/section/
 * step callers MUST pass an explicit scope so the picker narrows correctly.
 */
export function ConditionsEditorModal({
  open,
  title,
  intro,
  initialConditions,
  initialCombinator,
  allSteps,
  scope = { kind: 'pdf' },
  saving,
  onCancel,
  onSave,
}: {
  open: boolean;
  /** Modal header text. Pickers vary per caller (e.g. "Populate this PDF
   *  only if…" vs "Show this field only if…"). */
  title: string;
  /** One-line description that pairs with the modal title. */
  intro: string;
  initialConditions: FileEntryCondition[];
  initialCombinator: 'all' | 'any' | null;
  allSteps: WorkflowStep[];
  /** See `ConditionScope` — narrows the source-field picker. */
  scope?: ConditionScope;
  saving?: boolean;
  onCancel: () => void;
  /** Caller persists; modal closes on success. Empty list + null combinator
   *  clears the gating. */
  onSave: (
    conditions: FileEntryCondition[],
    combinator: 'all' | 'any' | null,
  ) => void | Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language.startsWith('fr');
  const I18N_NS = 'workflow.conditionsEditor';

  // Selectable source fields, grouped by owning DynamicForm step (cascading
  // Step → Field selects keep the modal navigable at scale). The scope filter
  // is applied during the walk, so the picker never even reveals a field this
  // callsite isn't allowed to gate on — see `ConditionScope` for the rules.
  const stepsWithFields = useMemo(
    () => eligibleSourceFields(allSteps, scope, isFr),
    [allSteps, isFr, scope],
  );

  const [draftConditions, setDraftConditions] = useState<FileEntryCondition[]>([]);
  const [combinator, setCombinator] = useState<'all' | 'any'>('all');

  // The "add a new condition" inline picker state.
  const [pickStepId, setPickStepId] = useState<string | null>(null);
  const [pickFieldId, setPickFieldId] = useState<string | null>(null);
  const [pickOperator, setPickOperator] = useState<ConditionOperator | null>(null);
  const [pickValue, setPickValue] = useState<string | null>(null);
  /** The range operator's exclusive upper bound. Null for every other operator,
   *  which is what keeps their saved rules byte-identical — see `pickToCondition`. */
  const [pickValueMax, setPickValueMax] = useState<string | null>(null);
  /** Quantifier / instance index for a source inside a repeatable section —
   *  `'any'`, `'all'`, or a decimal index. Null for an ordinary source, which
   *  is the only case where the condition reads a bare field id. */
  const [pickInstanceScope, setPickInstanceScope] = useState<string | null>(null);
  /** Index into the saved-rule list the picker is currently editing, or null
   *  when the picker is composing a brand-new rule. An edit keeps the row in
   *  the list (flagged, not removed) so abandoning the edit costs nothing and
   *  the rule keeps its original position when committed. */
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setDraftConditions(initialConditions);
    // Default combinator to "all" — most multi-condition rules are AND in
    // practice; originator can flip to "any" once there are 2+ rules.
    setCombinator(initialCombinator ?? 'all');
    setPickStepId(null);
    setPickFieldId(null);
    setPickOperator(null);
    setPickValue(null);
    setPickValueMax(null);
    setPickInstanceScope(null);
    setEditingIndex(null);
    // initialConditions / initialCombinator intentionally excluded from deps
    // so reopening the modal with a different reference doesn't wipe an
    // in-progress draft mid-edit. We only re-seed on open transitions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pickStep = stepsWithFields.find((s) => s.stepId === pickStepId) ?? null;
  // A picked source is either a field or a repeatable section (encoded as
  // `section:<id>` in `pickFieldId` — see `sectionIdFromSourceValue`).
  const pickSectionId = pickFieldId !== null ? sectionIdFromSourceValue(pickFieldId) : null;
  const pickSection: EligibleSection | null =
    pickSectionId !== null
      ? (pickStep?.sections.find((s) => s.id === pickSectionId) ?? null)
      : null;
  const pickField =
    pickSectionId === null ? (pickStep?.fields.find((f) => f.id === pickFieldId) ?? null) : null;
  const pickOperators = pickSection ? COUNT_OPERATORS : operatorsFor(pickField);
  const pickValueOptions: SelectOption[] | null = (() => {
    if (pickSection) return null;
    if (!pickField) return null;
    if (pickField.type === 'Toggle' || pickField.type === 'Checkbox') return null;
    // Date sources use the numeric years input below, not an options list —
    // a DOB field carries no `options` to pick from.
    if (pickField.type === 'Date') return null;
    return pickField.options;
  })();
  const pickIsAgeOperator = pickOperator !== null && AGE_OPERATORS.has(pickOperator);
  const pickIsAgeRangeOperator = pickOperator === AGE_RANGE_OPERATOR;
  const pickIsCountOperator = pickOperator !== null && COUNT_OPERATORS.includes(pickOperator);
  /** The repeatable section the picked FIELD lives in, if any. A count source is
   *  excluded: `count_eq`/`count_ge` read the section's own instance count, which is
   *  already one number per section — quantifying it would be meaningless. */
  const pickRepeatableOwner =
    pickField && pickStep ? (pickStep.repeatableOwners[pickField.id] ?? null) : null;

  /** A range is only complete once BOTH bounds are in and the upper one is actually above
   *  the lower. An empty range (`max <= min`) is satisfiable by nobody, so it is never what
   *  was meant — and as a silently-always-false gate it would be invisible in the builder.
   *  The BE refuses it too; catching it here keeps the originator out of a 400. */
  const pickRangeComplete =
    !pickIsAgeRangeOperator ||
    (pickValue !== null &&
      pickValueMax !== null &&
      Number.parseInt(pickValueMax, 10) > Number.parseInt(pickValue, 10));

  const pickComplete =
    pickStepId !== null &&
    pickFieldId !== null &&
    pickOperator !== null &&
    (pickOperator === 'is_true' ||
      pickOperator === 'is_false' ||
      (pickValue !== null && pickValue !== '')) &&
    pickRangeComplete;

  /** The condition's real `fieldId` — a section's own id when a section was
   *  picked (see `count_eq`/`count_ge` in `evaluateConditions.ts`), never the
   *  `section:`-prefixed picker value. */
  const pickSourceFieldId = (): string | null => pickSectionId ?? pickFieldId;

  /** The rule the picker currently describes. Only meaningful once
   *  `pickComplete`.
   *
   *  `instanceScope` comes from the picker's own control, which exists only
   *  while the source is a field inside a repeatable section. It used to be
   *  carried over from the rule being edited instead, because nothing authored
   *  it — and that silently DROPPED the quantifier the moment the originator
   *  re-pointed a rule at a different field. On RDSP's "no accountholder is
   *  already the primary caregiver" (`not_equals yes` / `all`) that turned the
   *  rule into a bare read resolving false, which is the regression recorded at
   *  `evaluateConditions.ts`'s zero-instance branch. */
  const pickToCondition = (original?: FileEntryCondition): FileEntryCondition => ({
    fieldStepId: pickStepId!,
    fieldId: pickSourceFieldId()!,
    operator: pickOperator!,
    // Booleans pack the operator name in `value` so the BE store always
    // has a non-null string per-condition.
    value:
      pickOperator === 'is_true' || pickOperator === 'is_false' ? pickOperator : (pickValue ?? ''),
    // Only the range operator carries an upper bound, and it carries it from the
    // picker's own control — never from the rule being edited. Re-pointing a range
    // rule at a non-date field would otherwise strand a `valueMax` the new operator
    // has no use for, which the BE refuses outright (it is only valid alongside the
    // range operator). Same silent-drop hazard the quantifier below documents, with
    // the failure moved from "wrong gate" to "save rejected".
    ...(pickIsAgeRangeOperator && pickValueMax !== null ? { valueMax: pickValueMax } : {}),
    // Omitted entirely for an ordinary source, so a rule that never needed a
    // quantifier still saves byte-identical.
    ...(pickRepeatableOwner && pickInstanceScope !== null
      ? { instanceScope: pickInstanceScope as FileEntryCondition['instanceScope'] }
      : {}),
    // The row's and/or group is set from the LIST, not from this picker, so an
    // edit has to carry it back or committing silently returns the row to the
    // combinator's default — the same silent-drop the quantifier used to suffer,
    // and just as invisible, since a one-group rule looks identical either way.
    ...(original?.connector ? { connector: original.connector } : {}),
  });

  const clearPicker = () => {
    setPickStepId(null);
    setPickFieldId(null);
    setPickOperator(null);
    setPickValue(null);
    setPickValueMax(null);
    setPickInstanceScope(null);
    setEditingIndex(null);
  };

  /** Commit the picker — replacing the rule under edit in place, or appending
   *  a new rule when the picker is composing rather than editing. */
  const commitPick = () => {
    if (!pickComplete || !pickStepId || !pickFieldId || !pickOperator) return;
    const at = editingIndex;
    setDraftConditions((prev) =>
      at !== null && at < prev.length
        ? prev.map((c, i) => (i === at ? pickToCondition(c) : c))
        : [...prev, pickToCondition()],
    );
    clearPicker();
  };

  /** True once the originator has touched the picker at all. Drives the
   *  "this clears the rule in progress" warning on the Edit buttons — there's
   *  nothing to warn about when the picker is already empty. */
  const pickerDirty =
    pickStepId !== null ||
    pickFieldId !== null ||
    pickOperator !== null ||
    pickValue !== null ||
    pickValueMax !== null ||
    pickInstanceScope !== null;

  /** Whether a saved rule can be loaded back into the picker: only when its
   *  source is still reachable through THIS callsite's scope (see
   *  `ConditionScope`). A rule whose source lives outside the current scope —
   *  or was deleted from the form since — has no option to select, so the
   *  picker would silently drop the source on commit. Those rows keep a
   *  disabled Edit button and stay editable only by remove + re-add. */
  const editableSource = (c: FileEntryCondition): boolean => {
    const step = stepsWithFields.find((s) => s.stepId === c.fieldStepId);
    if (!step) return false;
    return COUNT_OPERATORS.includes(c.operator as ConditionOperator)
      ? step.sections.some((sec) => sec.id === c.fieldId)
      : step.fields.some((f) => f.id === c.fieldId);
  };

  /** Load a saved rule into the picker. Discards whatever the picker held —
   *  the Edit button warns first whenever that isn't empty. */
  const beginEdit = (index: number) => {
    const c = draftConditions[index];
    if (!c || !editableSource(c)) return;
    const isCount = COUNT_OPERATORS.includes(c.operator as ConditionOperator);
    setPickStepId(c.fieldStepId);
    // Count rules store the SECTION id bare in `fieldId`; the picker needs it
    // back in `section:`-prefixed form to match the Select's option value.
    setPickFieldId(isCount ? sectionSourceValue(c.fieldId) : c.fieldId);
    setPickOperator(c.operator as ConditionOperator);
    // Boolean rules pack the operator into `value` (see `pickToCondition`) —
    // they have no value input, so seeding it would leave a stale string.
    setPickValue(c.operator === 'is_true' || c.operator === 'is_false' ? null : c.value);
    setPickValueMax(c.valueMax ?? null);
    setPickInstanceScope(c.instanceScope ?? null);
    setEditingIndex(index);
  };

  /**
   * The connector a row evaluates under when it carries none of its own: the
   * list's combinator. Keeping that inheritance is what lets an untouched rule
   * save byte-identical — the control below only writes `connector` when the
   * originator actually picks one.
   */
  const inheritedConnector: 'and' | 'or' = combinator === 'any' ? 'or' : 'and';

  const setConnectorAt = (index: number, connector: 'and' | 'or') =>
    setDraftConditions((prev) => prev.map((c, i) => (i === index ? { ...c, connector } : c)));

  /** How many rows sit in each group, under the inheritance above. */
  const groups = draftConditions.reduce(
    (acc, c) => {
      acc[(c.connector ?? inheritedConnector) === 'or' ? 'or' : 'and'] += 1;
      return acc;
    },
    { and: 0, or: 0 },
  );
  /** A rule is only worth explaining when it actually spans both groups. */
  const isMixed = groups.and > 0 && groups.or > 0;

  const removeCondition = (index: number) => {
    setDraftConditions((prev) => prev.filter((_, i) => i !== index));
    // Keep `editingIndex` pointed at the same rule once the list shifts — and
    // abandon the edit outright when the removed row IS the one being edited,
    // so the picker can never commit back onto an unrelated rule.
    setEditingIndex((prev) => {
      if (prev === null || prev === index) return null;
      return prev > index ? prev - 1 : prev;
    });
  };

  /**
   * Read-only summary line for a saved condition row. Walks the FULL workflow
   * (`allSteps`) — not the scope-filtered `stepsWithFields` — so a condition
   * whose source field still exists OUTSIDE the current picker scope still
   * resolves to its real labels. Without this, saved rules referencing
   * fields outside the new scope would mislabel themselves as "(deleted)"
   * and tempt the originator to delete a perfectly-valid rule.
   */
  const summarise = (c: FileEntryCondition): string => {
    const step = allSteps.find((s) => s.id === c.fieldStepId);
    const stepLabel = step
      ? (isFr ? step.titleFr : step.titleEn) || step.titleEn || step.titleFr
      : t(`${I18N_NS}.missingRef`);
    const opLabel = t(`${I18N_NS}.op.${c.operator}`);

    // Count conditions key off a SECTION's instance count, not a field value
    // — `c.fieldId` is a sectionId (see evaluateConditions.ts). Resolve the
    // section's title instead of walking fields, and render the value as a
    // bare number (a threshold, never an options lookup).
    if (c.operator === 'count_eq' || c.operator === 'count_ge') {
      let section: { titleEn: string; titleFr: string } | undefined;
      if (step?.type === 'DynamicForm') {
        section = step.sections.find((sec) => sec.id === c.fieldId);
      }
      const sourceLabel = section
        ? (isFr ? section.titleFr : section.titleEn) || section.titleEn || section.titleFr
        : t(`${I18N_NS}.missingRef`);
      return `${stepLabel} — ${sourceLabel} ${opLabel} ${c.value}`;
    }

    let field: WorkflowField | undefined;
    if (step?.type === 'DynamicForm') {
      for (const sec of step.sections) {
        for (const row of sec.rows) {
          for (const col of row.columns) {
            if (col.field?.id === c.fieldId) field = col.field;
          }
        }
      }
    }
    const fieldLabel = field
      ? (isFr ? field.labelFr : field.labelEn) || field.labelEn || field.labelFr
      : t(`${I18N_NS}.missingRef`);
    // Without this, "Accountholder 1 is the primary caregiver" and "Accountholder 2 is"
    // summarise to the same sentence — the rows the index exists to tell apart.
    const scopeLabel = (() => {
      const scopeValue = c.instanceScope;
      if (!scopeValue) return '';
      if (scopeValue === 'any' || scopeValue === 'all')
        return ` (${t(`${I18N_NS}.instanceScope${scopeValue === 'any' ? 'Any' : 'All'}`)})`;
      let owner: FormSection | undefined;
      if (step?.type === 'DynamicForm') {
        owner = step.sections.find((sec) =>
          sec.rows.some((row) => row.columns.some((col) => col.field?.id === c.fieldId)),
        );
      }
      if (!owner || !/^\d+$/.test(scopeValue)) return ` (${scopeValue})`;
      return ` (${renderInstanceLabel(owner, Number.parseInt(scopeValue, 10), isFr ? 'fr' : 'en')})`;
    })();
    const valueLabel = (() => {
      if (c.operator === 'is_true' || c.operator === 'is_false') return '';
      // A range renders BOTH bounds. Rendering `c.value` alone would read as a plain
      // "age is at least 6" and hide the upper bound entirely — the half of the rule
      // this operator exists to carry.
      if (c.operator === AGE_RANGE_OPERATOR) {
        return ` ${t(`${I18N_NS}.ageRangeValue`, { min: c.value, max: c.valueMax ?? '?' })}`;
      }
      const opt = field?.options.find((o) => o.value === c.value);
      return opt
        ? ` ${(isFr ? opt.labelFr : opt.labelEn) || opt.labelEn || opt.labelFr}`
        : ` ${c.value}`;
    })();
    return `${stepLabel} — ${fieldLabel}${scopeLabel} ${opLabel}${valueLabel}`;
  };

  const handleSave = () => {
    // Auto-commit any complete in-progress rule before persisting. Without
    // this, originators who fill in the picker and click Save (instead of
    // Add rule → Save) lost their typed rule silently. We can't call
    // commitPick() and then read draftConditions in the same handler
    // (setState is async), so we merge the in-flight pick into a local
    // copy before handing off to onSave. An in-flight EDIT replaces its
    // original row rather than appending a duplicate.
    let finalConditions = draftConditions;
    if (pickComplete && pickStepId && pickFieldId && pickOperator) {
      finalConditions =
        editingIndex !== null && editingIndex < draftConditions.length
          ? draftConditions.map((c, i) => (i === editingIndex ? pickToCondition(c) : c))
          : [...draftConditions, pickToCondition()];
    }
    void onSave(finalConditions, finalConditions.length === 0 ? null : combinator);
  };

  return (
    <BmoModal
      open={open}
      title={title}
      onCancel={onCancel}
      width={620}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={saving}>
          {t('common.cancel')}
        </Button>,
        <Button key="save" type="primary" loading={!!saving} onClick={handleSave}>
          {t('common.save')}
        </Button>,
      ]}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {intro}
        </Text>

        {/* Combinator — hidden until you have 2+ rules; without two things
            to combine the toggle is moot. It is the DEFAULT each row falls back
            to, not the whole rule: a row can override it below. */}
        {draftConditions.length >= 2 && (
          <div>
            <label style={{ fontSize: 12, color: '#6b7280' }}>
              {t(`${I18N_NS}.combinatorLabel`)}
            </label>
            <Select<'all' | 'any'>
              style={{ width: '100%' }}
              value={combinator}
              onChange={setCombinator}
              options={[
                { value: 'all', label: t(`${I18N_NS}.combinatorAll`) },
                { value: 'any', label: t(`${I18N_NS}.combinatorAny`) },
              ]}
            />
          </div>
        )}

        {/* Spelled out only for a rule that actually mixes the two. "And" and
            "Or" on their own rows read as a left-to-right chain, which is NOT
            what happens — the AND rows are all required, and the OR rows are a
            single "at least one of these" requirement alongside them. */}
        {isMixed && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t(`${I18N_NS}.mixedExplainer`, { and: groups.and, or: groups.or })}
          </Text>
        )}

        {draftConditions.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: 12, color: '#6b7280' }}>{t(`${I18N_NS}.listLabel`)}</label>
            {draftConditions.map((c, i) => {
              const isEditing = editingIndex === i;
              const canEdit = editableSource(c);
              return (
                <div
                  key={`${c.fieldId}-${i}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 10px',
                    // The row under edit is tinted + accented so the picker
                    // below reads as "this row", not as a second, new rule.
                    border: `1px solid ${isEditing ? '#0179C1' : '#e5e7eb'}`,
                    borderRadius: 8,
                    background: isEditing ? '#eff6ff' : '#fff',
                  }}
                >
                  {/* Shown on EVERY row, including the first. Under this model a
                      connector picks which group the row belongs to rather than
                      how it joins to the row above, so the first row's choice is
                      as meaningful as any other's. Only rendered once there are
                      two rows — a lone rule has no grouping to express. */}
                  {draftConditions.length >= 2 && (
                    <Select<'and' | 'or'>
                      size="small"
                      style={{ width: 76 }}
                      aria-label={t(`${I18N_NS}.connectorLabel`)}
                      value={c.connector ?? inheritedConnector}
                      onChange={(v) => setConnectorAt(i, v)}
                      options={[
                        { value: 'and', label: t(`${I18N_NS}.connectorAnd`) },
                        { value: 'or', label: t(`${I18N_NS}.connectorOr`) },
                      ]}
                    />
                  )}
                  <span style={{ flex: 1, fontSize: 13, color: '#111827' }}>{summarise(c)}</span>
                  {isEditing && (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {t(`${I18N_NS}.editingBadge`)}
                    </Text>
                  )}
                  {/* Span wrapper: AntD strips pointer events from a disabled
                      Button, which would swallow the tooltip explaining WHY
                      it's disabled. */}
                  <Tooltip title={canEdit ? t(`${I18N_NS}.edit`) : t(`${I18N_NS}.editUnavailable`)}>
                    <span style={{ display: 'inline-flex' }}>
                      {/* The warning only gates the case it describes: with an
                          untouched picker there's nothing to clear, so the
                          Popconfirm is disabled and the button acts directly. */}
                      <ConfirmPopover
                        title={t(`${I18N_NS}.editConfirmTitle`)}
                        description={t(`${I18N_NS}.editConfirmDesc`)}
                        disabled={!canEdit || !pickerDirty}
                        onConfirm={() => beginEdit(i)}
                      >
                        <Button
                          {...iconBtn}
                          aria-label={t(`${I18N_NS}.edit`)}
                          disabled={!canEdit}
                          icon={<EditOutlined />}
                          onClick={() => {
                            // Popconfirm owns the click when it's armed —
                            // acting here too would edit before confirming.
                            if (pickerDirty) return;
                            beginEdit(i);
                          }}
                        />
                      </ConfirmPopover>
                    </span>
                  </Tooltip>
                  <Tooltip title={t(`${I18N_NS}.remove`)}>
                    <Button
                      {...iconBtn}
                      aria-label={t(`${I18N_NS}.remove`)}
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeCondition(i)}
                    />
                  </Tooltip>
                </div>
              );
            })}
          </div>
        )}

        {/* Inline picker — Step → Field → Operator → Value. The "Add" button
            stays disabled until all four are picked. */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: 12,
            border: `1px dashed ${editingIndex !== null ? '#0179C1' : '#cbd5e1'}`,
            borderRadius: 8,
            background: '#f8fafc',
          }}
        >
          <Text strong style={{ fontSize: 12 }}>
            {t(`${I18N_NS}.${editingIndex !== null ? 'editTitle' : 'addTitle'}`)}
          </Text>
          {/* No eligible source fields for this scope — tell the user why
              instead of just leaving them with a greyed-out dropdown. */}
          {stepsWithFields.length === 0 && (
            <div
              style={{
                fontSize: 12,
                color: '#92400e',
                background: '#fef3c7',
                border: '1px solid #fcd34d',
                borderRadius: 6,
                padding: '6px 8px',
              }}
            >
              {t(`${I18N_NS}.emptyForScope.${scope.kind}`)}
            </div>
          )}
          <Select<string>
            style={{ width: '100%' }}
            value={pickStepId ?? undefined}
            placeholder={t(`${I18N_NS}.stepPlaceholder`)}
            disabled={stepsWithFields.length === 0}
            onChange={(next) => {
              setPickStepId(next);
              setPickFieldId(null);
              setPickOperator(null);
              setPickValue(null);
              setPickValueMax(null);
            }}
            showSearch
            optionFilterProp="label"
            options={stepsWithFields.map((s) => ({ value: s.stepId, label: s.stepTitle }))}
            notFoundContent={t(`${I18N_NS}.noFields`)}
          />
          <Select<string>
            style={{ width: '100%' }}
            value={pickFieldId ?? undefined}
            placeholder={t(`${I18N_NS}.fieldPlaceholder`)}
            disabled={!pickStep}
            onChange={(next) => {
              setPickFieldId(next);
              setPickOperator(null);
              setPickValue(null);
              setPickValueMax(null);
              // A source inside a repeatable section has NO bare value to read, so
              // leaving the scope unset would silently resolve `undefined` — the
              // target hidden forever under a positive operator, shown forever under
              // a negative one. "Any instance" is the safe, most-common reading;
              // the control below is where the originator narrows it.
              setPickInstanceScope(
                sectionIdFromSourceValue(next) === null && pickStep?.repeatableOwners[next]
                  ? 'any'
                  : null,
              );
            }}
            showSearch
            optionFilterProp="label"
            options={(() => {
              const fieldOptions = (pickStep?.fields ?? []).map((field) => ({
                value: field.id,
                label: (isFr ? field.labelFr : field.labelEn) || field.labelEn || field.labelFr,
              }));
              const sectionOptions = (pickStep?.sections ?? []).map((section) => ({
                value: sectionSourceValue(section.id),
                label:
                  (isFr ? section.titleFr : section.titleEn) || section.titleEn || section.titleFr,
              }));
              // Grouped options only when there's actually a repeatable section to
              // group — keeps the flat, ungrouped list for every step that has none
              // (the overwhelming majority) exactly as it rendered before this.
              return sectionOptions.length > 0
                ? [
                    { label: t(`${I18N_NS}.fieldsGroupLabel`), options: fieldOptions },
                    { label: t(`${I18N_NS}.sectionsGroupLabel`), options: sectionOptions },
                  ]
                : fieldOptions;
            })()}
          />
          {/* Instance scope — only for a field inside a repeatable section, which is
              the only source with more than one value to compare against. */}
          {pickRepeatableOwner && (
            <Select<string>
              style={{ width: '100%' }}
              value={pickInstanceScope ?? undefined}
              aria-label={t(`${I18N_NS}.instanceScopeLabel`)}
              placeholder={t(`${I18N_NS}.instanceScopeLabel`)}
              onChange={setPickInstanceScope}
              options={instanceScopeOptions(
                pickRepeatableOwner,
                pickInstanceScope,
                isFr ? 'fr' : 'en',
                t,
                I18N_NS,
              )}
            />
          )}
          <Select<ConditionOperator>
            style={{ width: '100%' }}
            value={pickOperator ?? undefined}
            placeholder={t(`${I18N_NS}.operatorPlaceholder`)}
            disabled={!pickField && !pickSection}
            onChange={(op) => {
              setPickOperator(op);
              if (op === 'is_true' || op === 'is_false') setPickValue(null);
              // Only the range operator has an upper bound. Leaving one behind after a
              // switch would hand `pickToCondition` a bound the new operator cannot carry,
              // and the BE refuses a ValueMax beside anything but the range operator.
              if (op !== AGE_RANGE_OPERATOR) setPickValueMax(null);
            }}
            options={pickOperators.map((op) => ({
              value: op,
              label: t(`${I18N_NS}.op.${op}`),
            }))}
          />
          {pickValueOptions && (
            <Select<string>
              style={{ width: '100%' }}
              value={pickValue ?? undefined}
              placeholder={t(`${I18N_NS}.valuePlaceholder`)}
              disabled={!pickOperator}
              onChange={setPickValue}
              options={pickValueOptions.map((o) => ({
                value: o.value,
                label: (isFr ? o.labelFr : o.labelEn) || o.labelEn || o.labelFr,
              }))}
            />
          )}
          {pickIsAgeOperator && (
            <InputNumber<number>
              style={{ width: '100%' }}
              value={pickValue !== null ? Number(pickValue) : undefined}
              aria-label={t(
                `${I18N_NS}.${pickIsAgeRangeOperator ? 'yearsMinPlaceholder' : 'yearsPlaceholder'}`,
              )}
              placeholder={t(
                `${I18N_NS}.${pickIsAgeRangeOperator ? 'yearsMinPlaceholder' : 'yearsPlaceholder'}`,
              )}
              min={0}
              precision={0}
              // Strict-integer parse mirrors the evaluator's own guard
              // (evaluateConditions.ts) — a non-integer or empty entry
              // clears the value so "Add rule" stays disabled rather than
              // silently persisting a threshold the runtime would reject.
              onChange={(next) => setPickValue(next !== null ? String(next) : null)}
            />
          )}
          {/* The range's EXCLUSIVE upper bound, beside the inclusive lower one above.
              Exclusive because that is what the rules being authored actually say: "up
              to the day before their 9th birthday" is `< 9`, and an inclusive bound
              would make every such rule read one year wider than it is. */}
          {pickIsAgeRangeOperator && (
            <InputNumber<number>
              style={{ width: '100%' }}
              value={pickValueMax !== null ? Number(pickValueMax) : undefined}
              aria-label={t(`${I18N_NS}.yearsMaxPlaceholder`)}
              placeholder={t(`${I18N_NS}.yearsMaxPlaceholder`)}
              min={0}
              precision={0}
              onChange={(next) => setPickValueMax(next !== null ? String(next) : null)}
            />
          )}
          {/* Both bounds entered but the range is empty — say so, rather than leaving the
              originator staring at a disabled "Add rule" with no reason given. */}
          {pickIsAgeRangeOperator &&
            !pickRangeComplete &&
            pickValue !== null &&
            pickValueMax !== null && (
              <Text type="danger" style={{ fontSize: 12 }}>
                {t(`${I18N_NS}.ageRangeInvalid`)}
              </Text>
            )}
          {pickIsCountOperator && (
            <InputNumber<number>
              style={{ width: '100%' }}
              value={pickValue !== null ? Number(pickValue) : undefined}
              placeholder={t(`${I18N_NS}.countPlaceholder`)}
              min={0}
              precision={0}
              // Same strict-integer parse as the age-years input above — a
              // non-integer or empty entry clears the value so "Add rule"
              // stays disabled rather than persisting a threshold the
              // evaluator's own regex guard would reject.
              onChange={(next) => setPickValue(next !== null ? String(next) : null)}
            />
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              type="dashed"
              style={{ flex: 1 }}
              icon={editingIndex !== null ? <EditOutlined /> : <PlusOutlined />}
              disabled={!pickComplete}
              onClick={commitPick}
            >
              {t(`${I18N_NS}.${editingIndex !== null ? 'updateButton' : 'addButton'}`)}
            </Button>
            {/* Abandon an edit without touching the rule — it stayed in the
                list the whole time, so this just empties the picker. */}
            {editingIndex !== null && (
              <Button type="text" onClick={clearPicker}>
                {t(`${I18N_NS}.cancelEdit`)}
              </Button>
            )}
          </div>
        </div>
      </div>
    </BmoModal>
  );
}
